import React, { useCallback, useEffect, useRef } from 'react';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { useLazyQuery, useMutation } from '@apollo/client';
import {
  FormInput,
  gqlAdd,
  gqlDelete,
  gqlGetByIdQuery,
  gqlUpdate,
  RouteLineInput,
} from '../graphql';
import * as Lo from 'lodash';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  YupNumeric,
  YupDate,
  YupString,
  YupBoolean,
  promiseModalDialog,
  isNumeric,
  toastSuccess,
  YupObject,
} from 'motion-components';

import FormFields from './form-fields';
import { useHandleError } from 'shared-pages/services';

import { requiredString } from 'shared-pages/common/constants';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { CollectionOrderStates } from 'shared-pages/common/constants';
import { useRootStore } from 'store';
import { FormCard } from 'motion-components';
import { useFormPermissions } from '../form-permissions';

export const RouteFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const { handleGraphqlError } = useHandleError('route');
  const history = useHistory();
  const { appStore } = useRootStore();
  const [updateRecord] = useMutation(gqlUpdate);
  const [deleteRecord] = useMutation(gqlDelete);
  const [addNewRecord] = useMutation(gqlAdd);
  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetByIdQuery, {
    fetchPolicy: 'network-only',
  });

  const formikRef = useRef<any>(null);
  const idNum = isNumeric(id) ? parseInt(id) : -1;
  const isNew = idNum < 0;
  const data = rawData ? rawData[Object.keys(rawData)[0]] : undefined;
  const pickProperties = (values) => {
    const picked = Lo.pick(values, Lo.keys(formShape));
    // Ensure routesLines is always an array
    if (!picked.routesLines) {
      picked.routesLines = [];
    }
    return picked;
  };
  //reset state so when the user cancels will remove the unregistered changes

  const { t } = useTranslation();

  /* user Permissions */
  const { canView, canViewReasoning } = useFormPermissions(data);

  useEffect(() => {
    if (!isNew) {
      get({ variables: { id: idNum } });
    }
  }, [get, id, idNum]);

  const formShape = {
    code: YupString,
    id: YupNumeric,
    routeDate: YupDate.required(t(requiredString)),
    dateCompleted: YupDate,
    vehicleId: YupNumeric.required(t(requiredString)),
    driverId: YupNumeric.required(t(requiredString)),
    notes: YupString,
    state: YupObject,
    statusId: YupNumeric,
    vehicle: Yup.object().shape({
      id: YupNumeric,
      plateNumber: YupString,
      collector: Yup.object()
        .shape({
          id: YupNumeric.nullable(),
          name: YupString.nullable(),
        })
        .nullable(),
    }),
    driver: YupObject,
    isActive: YupBoolean,
    routesLines: Yup.array().of(
      Yup.object().shape({
        aa: YupNumeric,
        id: YupNumeric.nullable(),
        collectionOrderId: YupNumeric,
        isActive: YupBoolean,
        notes: YupString,
        statusId: YupNumeric,
        collectionOrder: Yup.object().shape({
          code: YupString,
          collectionPointId: YupNumeric,
          id: YupNumeric,
          notes: YupString,
          collectionpoint: Yup.object().shape({
            name: YupString,
            addressArea: YupString,
            addressCity: YupString,
            addressDistrict: YupString,
            addressLine1: YupString,
            addressLine2: YupString,
            addressNomos: YupString,
            id: YupNumeric,
          }),
        }),
      }),
    ),
  };
  const validationSchema = Yup.object().shape(formShape);
  type FormValuesType = Yup.InferType<typeof validationSchema>;
  //------------------------------------INITIAL VALUES  -----------------------------------
  const defaultInitialValuesRef = React.useRef<FormValuesType>({
    isActive: true,
    routeDate: new Date(),
    routesLines: [],
  } as any);
  const fetched = React.useMemo<FormValuesType | null>(() => {
    if (!rawData?.getRouteById) return null;
    return pickProperties(rawData.getRouteById);
  }, [rawData?.getRouteById]); // reference changes only when Apollo result changes

  const initialValues: FormValuesType = isNew
    ? defaultInitialValuesRef.current
    : fetched ?? defaultInitialValuesRef.current;

  /**
   * * ΑΠΟΘΗΚΕΥΣΗ - UPDATE/CREATE
   */
  const title = isNew ? 'Νέο Δρομολόγιο' : `Δρομολόγιο: ${data?.code ?? ''}`;

  const handleSubmit = async (values: FormValuesType, actions: FormikHelpers<FormValuesType>) => {
    //const mutValues: any = removeTypename(Lo.pick(values, Lo.keys(new FormInput())));
    const mutValues: any = Lo.pick(values, Lo.keys(new FormInput()));

    //need to parse mutValues.routesLines and reshape it removing collectionOrder
    if (mutValues.routesLines) {
      mutValues.routesLines = mutValues.routesLines.map((r) => {
        return {
          ...Lo.pick(r, Lo.keys(new RouteLineInput())),
        };
      });
    }

    if (isNew) {
      console.log(
        '[Route] Add:',
        {
          ...mutValues,
        },
        values,
      );
      try {
        const d = await addNewRecord({
          variables: { data: mutValues },
        });
        const id = d.data[Object.keys(d.data)[0]].id;
        toastSuccess(`Επιτυχημένη Προσθήκη Εγγραφής [${id}]`);
        // goto new rec
        history.push({ pathname: generatePath(getRouteByName('Route'), { id: id }) });
      } catch (error) {
        handleGraphqlError('mutation-add ', error);
      }
    } else {
      console.log('[Applog] Update:', mutValues);
      try {
        console.log('$$$ mutValues', mutValues);
        const d = await updateRecord({ variables: { data: { ...mutValues, id: idNum } } });
        toastSuccess(`Επιτυχημένη Ενημέρωση Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);

        history.push({ pathname: generatePath(getRouteByName('Route'), { id: id }) });
      } catch (error) {
        handleGraphqlError('mutation-add ', error);
      }
    }
  };
  const handleDelete = useCallback(async () => {
    const dlg = await promiseModalDialog({
      title: 'Διαγραφή Δρομολογίου',
      text: 'Είστε σίγουροι ότι θέλετε να διαγράψετε το Δρομολόγιο;',
    });
    if (dlg) {
      try {
        const d = await deleteRecord({ variables: { id: idNum } });
        toastSuccess(`Επιτυχημένη Διαγραφή Δρομολογίου [${d.data[Object.keys(d.data)[0]].id}]`);
        history.push('/routes');
      } catch (error) {
        handleGraphqlError('mutation-delete', error);
      }
    }
  }, [deleteRecord, handleGraphqlError, history, idNum]);
  const canDelete = formikRef.current?.values?.statusId === CollectionOrderStates.NotRouted;

  /**
   * * RENDERING
   */
  if (error) {
    handleGraphqlError('query-get', error);
    return <h3 className="text-center">{error.message}</h3>;
  } else if (canView === false) {
    return <h3 className="text-center">{canViewReasoning}</h3>;
  } else {
    return (
      <>
        <FormCard
          title={title}
          key={isNew ? 'new' : `id-${idNum}`}
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          permitSubmit={false}
          permitDelete={canDelete}
          enableReinitialize={!isNew && !!fetched}
          idx={idNum}
          innerRef={formikRef}
        >
          <FormFields isNew={isNew} />
        </FormCard>
      </>
    );
  }
};
